import { Badge, Card, CardBody, Table, TabPane } from "reactstrap";
import DefaultButton from "../../../../../components/Buttons/Default";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import CreateBenefitTypeModal from "../Modals/CreateBenefitType";
import IconButton from "../../../../../components/Buttons/IconButton";
import { IoCreateOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import ConfirmationModal from "../../../../../components/Modals/Confirmation";
import { del } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import { useSettings } from "../../../../../context/settings";
import { describeBenefit } from "../../../utils/payroll";

/**
 * Renders the allowances or the deductions list depending on `type`, since both
 * are stored as benefit types and only differ by sign in the payroll run.
 */
function BenefitTypesTab({ tabId, type, benefitTypes, refreshSettings }) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [benefitTypeToDelete, setBenefitTypeToDelete] = useState(null);
  const [benefitTypeToEdit, setBenefitTypeToEdit] = useState(null);

  const currency = settings?.sales?.base_currency || '';
  const prefix = type === 'allowance' ? 'hr.settings.allowances' : 'hr.settings.deductions';

  const deleteBenefitType = async () => {
    const data = await del(`/hr/benefit-types/delete/${benefitTypeToDelete}`);
    if (data?.success) {
      refreshSettings();
      setBenefitTypeToDelete(null);
      toast.success(t('alerts.success_delete'));
    }
  }

  return (
    <TabPane tabId={tabId}>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted small fw-semibold text-uppercase mb-0">
              {t(`${prefix}.title`)}
            </p>
            <DefaultButton
              color="primary"
              size="sm"
              onClick={() => {
                setBenefitTypeToEdit(null);
                setCreateModalOpen(true);
              }}
            >
              + {t('common.create')}
            </DefaultButton>
          </div>

          <Table hover responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('hr.benefits.code')}</th>
                <th>{t('hr.benefits.calculation')}</th>
                <th>{t('hr.benefits.value')}</th>
                <th>{t('common.status')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!benefitTypes?.length && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    {t(`${prefix}.empty`)}
                  </td>
                </tr>
              )}
              {benefitTypes?.map(benefitType => (
                <tr key={benefitType?.id}>
                  <td>{benefitType?.name}</td>
                  <td>{benefitType?.code || '-'}</td>
                  <td className="text-muted small">{describeBenefit(benefitType, t)}</td>
                  <td>
                    {benefitType?.calculation_type === 'percentage'
                      ? `${benefitType?.value}%`
                      : `${benefitType?.value} ${currency}`}
                  </td>
                  <td>
                    <Badge color={benefitType?.is_active ? 'success' : 'secondary'}>
                      {benefitType?.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2 h-100">
                      <IconButton
                        size="sm"
                        outline
                        color="secondary"
                        onClick={() => {
                          setBenefitTypeToEdit(benefitType);
                          setCreateModalOpen(true);
                        }}
                      >
                        <IoCreateOutline />
                      </IconButton>
                      <IconButton
                        size="sm"
                        outline
                        color="danger"
                        onClick={() => setBenefitTypeToDelete(benefitType.id)}
                      >
                        <BsTrash3 />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <CreateBenefitTypeModal
        isOpen={createModalOpen}
        toggle={() => setCreateModalOpen(!createModalOpen)}
        benefitTypeToEdit={benefitTypeToEdit}
        refreshSettings={refreshSettings}
        type={type}
      />

      <ConfirmationModal
        isOpen={benefitTypeToDelete !== null}
        title={t(`${prefix}.deleteTitle`)}
        message={t(`${prefix}.deleteMessage`)}
        onConfirm={deleteBenefitType}
        toggle={() => setBenefitTypeToDelete(null)}
      />
    </TabPane>
  );
}

export default BenefitTypesTab;
